// PROD-2743 · the design sheet v1.1 on the encounter screens (docs/design-desk/
// APPLICATION-SHEET-practitioner-dev.md, 1 Oct 2026), DARK behind the frame's per-browser switch
// (`?ef=frameV2`, src/shared/lib/encounter-flags.ts).
//
// THE CONTRACT: switch OFF = today's app, byte for byte. Every consumer keeps its existing class
// string untouched and picks the `*_V2` string only when `useDesignV2()` is true. Nothing here
// edits a DS default: each V2 value is a DS token utility or a DS role/tone constant.
import { useState } from 'react';
import { cn, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** True only in a browser that has turned `frameV2` on. Read ONCE per mount, like the frame. */
export function useDesignV2(): boolean {
  const [on] = useState(() => isEncounterFlagOn('frameV2'));
  return on;
}

/**
 * §1 Type · caps label: the DS `section-eyebrow` role (12 / bold) in uppercase with the
 * section-heading tracking, inked `text-secondary` (labels whisper: 5.53:1; the role's default
 * `text-muted` is 3.30:1 and fails D-25). Merged through the DS `cn`, which knows the DS scale,
 * so the tone replaces the role's ink rather than racing it.
 */
export const V2_EYEBROW = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  TEXT_TONE_CLASSES.secondary,
  'uppercase tracking-section-heading',
);

/** §1 Type · a value against its label (13 / semibold, text-primary). */
export const V2_BODY_EMPHASIS = TEXT_ROLE_CLASSES['body-emphasis'];

/** §1 Type · body (13) in text-secondary: a quiet, honest line (an absent readout, "none yet"). */
export const V2_BODY_QUIET = cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.secondary);

/** §1 Type · meta (11, text-secondary where it carries information). */
export const V2_META = cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary);

/** §1 Status · an idle destructive icon is neutral at rest and red only on hover/confirm. */
export const V2_IDLE_DESTRUCTIVE = 'text-text-secondary hover:text-status-error';

/**
 * SYSTEM-RULES §2 · a CATEGORY label: the same caps-label role as `V2_EYEBROW`, inked with its
 * wayfinding INK (map v.2, 30 Sep: the rule takes the ink, never the fill — every ink is ≥ 4.79:1
 * on card, canvas and band). One meaning per colour, the same in the chart and the note:
 * Subjective/history → `history`, Plan/medications → `medications`. `neutral` needs no entry: its
 * ink (#5A6B75) IS `text-secondary`, so `V2_EYEBROW` already is the neutral label.
 * Literal class strings so Tailwind sees each one; the DS `cn` replaces the role's own ink.
 */
export const V2_EYEBROW_HISTORY = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  'uppercase tracking-section-heading',
  'text-wayfind-history-ink',
);
export const V2_EYEBROW_MEDICATIONS = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  'uppercase tracking-section-heading',
  'text-wayfind-medications-ink',
);
/** Map v.2 · Active / Resolved problems take the problems ink (blue-800, NOT the brand blue: brand
 *  is the ClinicalTermRow 'active' dot, so it cannot also be a category). */
export const V2_EYEBROW_PROBLEMS = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  'uppercase tracking-section-heading',
  'text-wayfind-problems-ink',
);
/** Map v.2 · Allergies: the one safety exception — the allergies ink (red-600), ON only. */
export const V2_EYEBROW_ALLERGIES = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  'uppercase tracking-section-heading',
  'text-wayfind-allergies-ink',
);
/** Map v.2 · Patient flags, Vaccinations, Occupation, Education. The neutral ink equals
 *  text-secondary today; it is named so the category stays a category if either token moves. */
export const V2_EYEBROW_NEUTRAL = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  'uppercase tracking-section-heading',
  'text-wayfind-neutral-ink',
);
